/**
 * 并发受限的请求池
 *
 * 背景：服务端当前走 HTTP/1.1，浏览器同源最多 6 个并发连接。
 * 统计页一次性发出 16+ 个请求（8 个聚合接口 + 8 个 /category），
 * 6 个慢请求占满连接后，其他页面（在线课程、菜单权限）全部排队几十秒，
 * 表现为「整个平台都卡」。
 *
 * 用法：
 *   const pool = createRequestPool(4)
 *   const res = await pool(() => getCollect({ regionId }))
 *   pool.stop() // 离开页面时丢弃排队中的请求
 */
export function createRequestPool(limit = 4) {
  let active = 0
  let stopped = false
  const queue = []

  const pump = () => {
    while (!stopped && active < limit && queue.length) {
      const task = queue.shift()
      active += 1
      task.run()
    }
  }

  const enqueue = (task) =>
    new Promise((resolve, reject) => {
      if (stopped) {
        reject(makeStoppedError())
        return
      }
      const run = () => {
        Promise.resolve()
          .then(task)
          .then(resolve, reject)
          .finally(() => {
            active -= 1
            pump()
          })
      }
      if (active < limit) {
        active += 1
        run()
      } else {
        queue.push({ run, reject })
      }
    })

  /** 丢弃排队中的请求，并拒绝后续入队（在途请求由 AbortController 取消） */
  enqueue.stop = (reason = '页面已离开，请求已取消') => {
    stopped = true
    while (queue.length) {
      const task = queue.shift()
      task.reject(makeStoppedError(reason))
    }
  }

  enqueue.stats = () => ({ active, pending: queue.length, limit })

  return enqueue
}

function makeStoppedError(reason = '请求已取消') {
  const error = new Error(reason)
  error.name = 'PoolStoppedError'
  return error
}

/** 判断错误是否属于「主动取消」，这类错误不应提示用户、也不应计入失败 */
export function isAbortError(error) {
  if (!error) return false
  return (
    error.name === 'AbortError' ||
    error.name === 'CanceledError' ||
    error.name === 'PoolStoppedError' ||
    error.code === 'ERR_CANCELED'
  )
}
